import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { actions } from 'store/guestInsights';
import {
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link
} from '@material-ui/core';
import { CloseIcon } from 'components';
import { ccToPcConverter } from '../../helpers/commonUtils';
import {
  convertToDateOnlyString,
  convertToDateTimeFormat2OnlyString,
  convertToDateFormat2OnlyString
} from '../../helpers/timeUtils';
import { Alert } from '@material-ui/lab';
import DisplayModal from 'components/DisplayModal';

const { getCurrentGuestDetail } = actions;

const PERSONAL_INFO_LABELS = {
  weddingAnniversaryDate: 'Wedding Anniversary Date',
  profileCreatedDate: 'Profile Create Date'
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  },
  closeIcon: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '0px'
  },
  labelDiv: {
    display: 'flex',
    margin: '8px 8px'
  },
  innerKeyDiv: {
    width: '400px',
    fontWeight: 500
  },
  innerValueDiv: {
    width: '500px',
    display: 'flex',
    alignItems: 'center'
  },

  ellipsisContainer: {
    flex: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },

  viewMore: {
    flexShrink: 0,
    marginLeft: 8,
    color: '#2e7d32',
    cursor: 'pointer',
    fontWeight: 500,
    textDecoration: 'none'
  },
  dialogTable: {
    maxHeight: '450px',
    overflowY: 'auto'
  },
  modalRow: {
    display: 'flex',
    marginBottom: 20
  },

  modalLabel: {
    width: '45%',
    fontWeight: 500,
    fontSize: '16px'
  },

  modalValue: {
    width: '55%',
    wordBreak: 'break-word',
    fontSize: '16px'
  },
  dialogHeader: {
    display: 'flex',
    backgroundColor: '#eceff1',
    borderBottom: '2px solid #bdbdbd',
    fontWeight: 700,
    padding: '12px 16px',
    fontSize: '13px',
    textTransform: 'uppercase',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },

  dialogScrollArea: {
    maxHeight: '50vh',
    overflowY: 'auto'
  },

  dialogRow: {
    display: 'flex',
    padding: '12px 16px',
    borderBottom: '1px solid #eeeeee',
    '&:nth-child(even)': {
      backgroundColor: '#fafafa'
    }
  },

  dialogIndexColumn: {
    width: '60px',
    flexShrink: 0,
    fontWeight: 500
  },

  dialogValueColumn: {
    flex: 1,
    wordBreak: 'break-word'
  },
  dialogCloseIcon: {
    color: '#f44336',
    cursor: 'pointer',
    fontSize: '28px',

    '&:hover': {
      color: '#d32f2f'
    }
  }
}));

const GuestInsightsDetails = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { guestProfileUuid } = useParams();
  const guestDetail = useSelector((store) => store.guestInsights.current);
  const isLoading = useSelector((store) => store.guestInsights.gettingCurrent);

  const [guestInfoValues, setGuestInfoValues] = useState({});
  const [contactInfoValues, setContactInfoValues] = useState({});
  const [consentInfoValues, setConsentInfoValues] = useState({});
  const [preferenceInfoValues, setPreferenceInfoValues] = useState({});
  const [additionGuestInfoValues, setAdditionGuestInfoValues] = useState({});
  const [activitySummaryValues, setActivitySummaryValues] = useState({});

  const navBack = () => {
    history.push('/guest-insights');
  };

  const getValuesFromGuestDetail = (guest) => {
    if (Object.keys(guest).length === 0) return;
    setGuestInfoValues({
      firstName: guest?.guestInformation?.firstName,
      middleName: guest?.guestInformation?.middleName,
      lastName: guest?.guestInformation?.lastName,
      nameSuffix: guest?.guestInformation?.nameSuffix,
      nameSalutation: guest?.guestInformation?.nameSalutation,
      gender: guest?.guestInformation?.gender,
      birthDate: convertToDateOnlyString(guest?.guestInformation?.birthDate),
      weddingAnniversaryDate: convertToDateOnlyString(
        guest?.guestInformation?.weddingAnniversaryDate
      ),
      profileCreatedDate: convertToDateTimeFormat2OnlyString(
        guest?.guestInformation?.profileCreatedDate || guest?.profileCreatedDate
      )
    });
    setContactInfoValues({
      primaryEmailAddress: guest?.guestInformation?.primaryEmailAddress,
      primaryPhoneNumber: guest?.guestInformation?.primaryPhoneNumber,
      secondaryEmailAddress: guest?.guestInformation?.emailAddresses,
      secondaryPhoneNumber: guest?.guestInformation?.phoneNumbers,
      addressLine1:
        guest?.guestInformation?.primaryMailingAddress?.addressLine1,
      addressLine2:
        guest?.guestInformation?.primaryMailingAddress?.addressLine2,
      addressLine3:
        guest?.guestInformation?.primaryMailingAddress?.addressLine3,
      city: guest?.guestInformation?.primaryMailingAddress?.city,
      region: guest?.guestInformation?.primaryMailingAddress?.region,
      postalCode: guest?.guestInformation?.primaryMailingAddress?.postalCode,
      countryCode: guest?.guestInformation?.primaryMailingAddress?.countryCode
    });
    setConsentInfoValues({
      emailPromotionalMessaging:
        guest?.guestInformation?.consentToEmailMessaging
          ?.consentToPromotionalMessaging,
      emailTransactionalMessaging:
        guest?.guestInformation?.consentToEmailMessaging
          ?.consentToTransactionalMessaging,
      smsPromotionalMessaging:
        guest?.guestInformation?.consentToSMSMessaging
          ?.consentToPromotionalMessaging,
      smsTransactionalMessaging:
        guest?.guestInformation?.consentToSMSMessaging
          ?.consentToTransactionalMessaging
    });
    guest?.preferenceInsights?.forEach((preference) => {
      setPreferenceInfoValues((prevState) => {
        return {
          ...prevState,
          ...(preference?.displayName && {
            [preference.displayName]: preference.value
          })
        };
      });
    });
    guest?.additionalGuestDataInsights?.map((additionalGuestData) => {
      setAdditionGuestInfoValues((prevState) => {
        return {
          ...prevState,
          ...(additionalGuestData?.displayName && {
            [additionalGuestData.displayName]: additionalGuestData.value
          })
        };
      });
    });
    setActivitySummaryValues({
      lastStayDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.stay?.lastStayDate?.activityDate
      ),
      nextStayDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.stay?.nextStayDate?.activityDate
      ),
      lastSpaDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.spa?.lastSpaDate?.activityDate
      ),
      nextSpaDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.spa?.nextSpaDate?.activityDate
      ),
      lastGolfDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.golf?.lastGolfDate?.activityDate
      ),
      nextGolfDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.golf?.nextGolfDate?.activityDate
      ),
      lastDiningDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.dining?.lastDiningDate?.activityDate
      ),
      nextDiningDate: convertToDateFormat2OnlyString(
        guest?.guestActivitySummary?.dining?.nextDiningDate?.activityDate
      )
    });
  };

  useEffect(() => {
    if (guestDetail?.guestProfileUuid !== guestProfileUuid) {
      dispatch(getCurrentGuestDetail({ data: [guestProfileUuid] }));
    }
  }, []);

  useEffect(() => {
    getValuesFromGuestDetail(guestDetail);
  }, [guestDetail]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogItems, setDialogItems] = useState([]);

  const openListDialog = (title, values) => {
    const formattedData = {
      Records: values.reduce((acc, item, index) => {
        acc[index + 1] = item;
        return acc;
      }, {})
    };

    setDialogTitle(title);
    setDialogItems(formattedData);
    setDialogOpen(true);
  };

  return (
    <div className={classes.root}>
      <div>
        {Object.keys(guestDetail).length === 0 && !isLoading && (
          <Alert severity="error" className={classes.alert}>
            No data available.
          </Alert>
        )}
      </div>
      <div className={classes.header}>
        <CloseIcon className={classes.closeIcon} onClickClose={navBack} />
      </div>
      <div className={classes.content}>
        {isLoading ? (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {guestInfoValues && Object.keys(guestInfoValues)?.length > 0 && (
              <div className={classes.content}>
                <Card>
                  <CardHeader
                    title="Guest - Personal Information"
                    titleTypographyProps={{ variant: 'h3' }}
                  />
                  <Divider />
                  <CardContent>
                    {Object.keys(guestInfoValues).map((prop) => {
                      return (
                        <div className={classes.labelDiv}>
                          <div className={classes.innerKeyDiv}>
                            {PERSONAL_INFO_LABELS[prop] ||
                              ccToPcConverter(prop)}
                          </div>
                          {guestInfoValues[prop] &&
                          typeof guestInfoValues[prop] === 'object' &&
                          guestInfoValues[prop]?.length > 0 ? (
                            guestInfoValues[prop].map((item, index) => (
                              <div
                                key={index}
                                className={classes.innerValueDiv}
                              >
                                {item}
                              </div>
                            ))
                          ) : (
                            <div className={classes.innerValueDiv}>
                              {guestInfoValues[prop] || '-'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            )}
            {contactInfoValues &&
              Object.keys(contactInfoValues)?.length > 0 && (
                <div className={classes.content}>
                  <Card>
                    <CardHeader
                      title="Guest - Contact Information"
                      titleTypographyProps={{ variant: 'h3' }}
                    />
                    <Divider />
                    <CardContent>
                      {Object.keys(contactInfoValues).map((prop) => {
                        return (
                          <div className={classes.labelDiv}>
                            <div className={classes.innerKeyDiv}>
                              {ccToPcConverter(prop)}
                            </div>
                            {Array.isArray(contactInfoValues[prop]) ? (
                              <div className={classes.innerValueDiv}>
                                {prop === 'secondaryEmailAddress' ||
                                prop === 'secondaryPhoneNumber' ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div className={classes.ellipsisContainer}>
                                      {contactInfoValues[prop][0]}
                                    </div>

                                    {contactInfoValues[prop].length > 1 && (
                                      <Link
                                        component="button"
                                        className={classes.viewMore}
                                        onClick={() =>
                                          openListDialog(
                                            ccToPcConverter(prop),
                                            contactInfoValues[prop]
                                          )
                                        }
                                      >
                                        +({contactInfoValues[prop].length - 1})
                                        More
                                      </Link>
                                    )}
                                  </div>
                                ) : (
                                  contactInfoValues[prop].map((item, index) => (
                                    <div key={index}>{item}</div>
                                  ))
                                )}
                              </div>
                            ) : (
                              <div className={classes.innerValueDiv}>
                                {contactInfoValues[prop] || '-'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              )}
            {consentInfoValues &&
              Object.keys(consentInfoValues)?.length > 0 && (
                <div className={classes.content}>
                  <Card>
                    <CardHeader
                      title="Guest - Consent Information"
                      titleTypographyProps={{ variant: 'h3' }}
                    />
                    <Divider />
                    <CardContent>
                      {Object.keys(consentInfoValues).map((prop) => {
                        return (
                          <div className={classes.labelDiv}>
                            <div className={classes.innerKeyDiv}>
                              {ccToPcConverter(prop)}
                            </div>
                            {typeof consentInfoValues[prop] === 'boolean' ? (
                              <div className={classes.innerValueDiv}>
                                {`${consentInfoValues[prop]}`}
                              </div>
                            ) : (
                              <div>{'-'}</div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              )}
            {preferenceInfoValues &&
              Object.keys(preferenceInfoValues)?.length > 0 && (
                <div className={classes.content}>
                  <Card>
                    <CardHeader
                      title="Preference Insight"
                      titleTypographyProps={{ variant: 'h3' }}
                    />
                    <Divider />
                    <CardContent>
                      {Object.keys(preferenceInfoValues).map((prop) => {
                        return (
                          <div className={classes.labelDiv}>
                            <div className={classes.innerKeyDiv}>
                              {ccToPcConverter(prop)}
                            </div>
                            {preferenceInfoValues[prop] &&
                            typeof preferenceInfoValues[prop] === 'object' &&
                            preferenceInfoValues[prop]?.length > 0 ? (
                              preferenceInfoValues[prop].map((item, index) => (
                                <div
                                  key={index}
                                  className={classes.innerValueDiv}
                                >
                                  {item}
                                </div>
                              ))
                            ) : (
                              <div className={classes.innerValueDiv}>
                                {preferenceInfoValues[prop] || '-'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              )}
            {additionGuestInfoValues &&
              Object.keys(additionGuestInfoValues)?.length > 0 && (
                <div className={classes.content}>
                  <Card>
                    <CardHeader
                      title="Additional Guest Data Insight"
                      titleTypographyProps={{ variant: 'h3' }}
                    />
                    <Divider />
                    <CardContent>
                      {Object.keys(additionGuestInfoValues).map((prop) => {
                        return (
                          <div className={classes.labelDiv}>
                            <div className={classes.innerKeyDiv}>
                              {ccToPcConverter(prop)}
                            </div>
                            {additionGuestInfoValues[prop] &&
                            typeof additionGuestInfoValues[prop] === 'object' &&
                            additionGuestInfoValues[prop]?.length > 0 ? (
                              additionGuestInfoValues[prop].map(
                                (item, index) => (
                                  <div
                                    key={index}
                                    className={classes.innerValueDiv}
                                  >
                                    {item}
                                  </div>
                                )
                              )
                            ) : (
                              <div className={classes.innerValueDiv}>
                                {additionGuestInfoValues[prop] || '-'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              )}
            {activitySummaryValues &&
              Object.keys(activitySummaryValues)?.length > 0 && (
                <div className={classes.content}>
                  <Card>
                    <CardHeader
                      title="Guest - Activity Summary"
                      titleTypographyProps={{ variant: 'h3' }}
                    />
                    <Divider />
                    <CardContent>
                      {Object.keys(activitySummaryValues).map((prop) => {
                        return (
                          <div className={classes.labelDiv}>
                            <div className={classes.innerKeyDiv}>
                              {ccToPcConverter(prop)}
                            </div>
                            {activitySummaryValues[prop] &&
                            typeof activitySummaryValues[prop] === 'object' &&
                            activitySummaryValues[prop]?.length > 0 ? (
                              activitySummaryValues[prop].map((item, index) => (
                                <div
                                  key={index}
                                  className={classes.innerValueDiv}
                                >
                                  {item}
                                </div>
                              ))
                            ) : (
                              <div className={classes.innerValueDiv}>
                                {activitySummaryValues[prop] || '-'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>
              )}
          </>
        )}
      </div>
      <DisplayModal
        isOpen={dialogOpen}
        setIsOpen={setDialogOpen}
        title={dialogTitle}
        batch={dialogItems}
        isLoading={false}
        size="sm"
        isConversionRequired={false}
        isIndentationRequired={false}
        showNumberedInnerList={true}
        enableAlternateRowColors={true}
        hideParentKeys={true}
        keyColumnWidth={400}
        innerListKeyColumnWidth={50}
        enableCopyOnHover={true}
      />
    </div>
  );
};

export default GuestInsightsDetails;
